import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../services/api";
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from "../../utils/imageUtils";
import "./OrdersManagement.css";

export default function OrdersManagement() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("");
  const [filterTotalMin, setFilterTotalMin] = useState("");
  const [filterTotalMax, setFilterTotalMax] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [lastOrderTimestamp, setLastOrderTimestamp] = useState(null);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const lastOrderCountRef = useRef(0);
  const lastOrderTimestampRef = useRef(null);

  // Initialize last order count and timestamp after first load
  useEffect(() => {
    if (orders.length > 0 && lastOrderCountRef.current === 0) {
      const count = orders.length;
      setLastOrderCount(count);
      lastOrderCountRef.current = count;
      
      // Get the most recent order timestamp
      const mostRecentOrder = orders.reduce((latest, order) => {
        const orderTime = new Date(order.createdAt).getTime();
        const latestTime = latest ? new Date(latest.createdAt).getTime() : 0;
        return orderTime > latestTime ? order : latest;
      }, null);
      if (mostRecentOrder) {
        const timestamp = new Date(mostRecentOrder.createdAt).getTime();
        setLastOrderTimestamp(timestamp);
        lastOrderTimestampRef.current = timestamp;
      }
    }
  }, [orders]);

  const checkForNewOrders = async () => {
    try {
      const ordersData = await api.getAllOrders();
      const currentCount = ordersData.length;
      const prevCount = lastOrderCountRef.current;
      const prevTimestamp = lastOrderTimestampRef.current;
      
      // Check if there are new orders
      if (currentCount > prevCount) {
        const newOrdersCount = currentCount - prevCount;
        
        // Get the most recent order
        const mostRecentOrder = ordersData.reduce((latest, order) => {
          const orderTime = new Date(order.createdAt).getTime();
          const latestTime = latest ? new Date(latest.createdAt).getTime() : 0;
          return orderTime > latestTime ? order : latest;
        }, null);

        // Only alert if the new order is actually newer than what we've seen
        if (mostRecentOrder) {
          const newOrderTime = new Date(mostRecentOrder.createdAt).getTime();
          
          // Check if this is a genuinely new order
          if (!prevTimestamp || newOrderTime > prevTimestamp) {
            // Show browser notification if permission granted
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('New Order Received! 🎉', {
                body: `${newOrdersCount} new order${newOrdersCount > 1 ? 's' : ''} has been placed. Order ID: #${mostRecentOrder._id.slice(-8).toUpperCase()}`,
                icon: '/favicon.ico',
                tag: 'new-order',
                requireInteraction: false
              });
            }

            // Show in-page alert
            setNewOrderAlert({
              count: newOrdersCount,
              orderId: mostRecentOrder._id,
              message: `${newOrdersCount} new order${newOrdersCount > 1 ? 's' : ''} received!`
            });

            // Auto-hide alert after 10 seconds
            setTimeout(() => {
              setNewOrderAlert(null);
            }, 10000);

            // Update refs and state
            lastOrderCountRef.current = currentCount;
            lastOrderTimestampRef.current = newOrderTime;
            setLastOrderCount(currentCount);
            setLastOrderTimestamp(newOrderTime);
            
            // Reload orders to show the new ones
            loadOrders();
          }
        }
      } else if (currentCount !== prevCount) {
        // Update count if it changed (e.g., order deleted)
        lastOrderCountRef.current = currentCount;
        setLastOrderCount(currentCount);
      }
    } catch (error) {
      console.error('Error checking for new orders:', error);
    }
  };

  useEffect(() => {
    loadOrders();
    
    // Set up polling to check for new orders every 30 seconds
    const pollInterval = setInterval(() => {
      checkForNewOrders();
    }, 30000); // Check every 30 seconds

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => clearInterval(pollInterval);
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, statusFilter, searchQuery, filterPaymentMethod, filterTotalMin, filterTotalMax, filterDateFrom, filterDateTo]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const ordersData = await api.getAllOrders();
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
      alert('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => {
        const orderId = order._id?.toLowerCase() || "";
        const customerName = (order.user?.name || "").toLowerCase();
        const customerEmail = (order.user?.email || "").toLowerCase();
        const customerPhone = (order.user?.phone || "").toLowerCase();
        const itemNames = order.items?.map(item => 
          (item.title || item.name || "").toLowerCase()
        ).join(" ") || "";
        
        return orderId.includes(query) ||
               customerName.includes(query) ||
               customerEmail.includes(query) ||
               customerPhone.includes(query) ||
               itemNames.includes(query);
      });
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Payment method filter
    if (filterPaymentMethod) {
      filtered = filtered.filter(order => order.paymentMethod === filterPaymentMethod);
    }

    // Total amount range filter
    if (filterTotalMin) {
      filtered = filtered.filter(order => (order.total || 0) >= parseFloat(filterTotalMin));
    }
    if (filterTotalMax) {
      filtered = filtered.filter(order => (order.total || 0) <= parseFloat(filterTotalMax));
    }

    // Date range filter
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= fromDate;
      });
    }
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate <= toDate;
      });
    }

    setFilteredOrders(filtered);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setFilterPaymentMethod("");
    setFilterTotalMin("");
    setFilterTotalMax("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedOrder) return;

    setUpdatingStatus(true);
    try {
      await api.updateOrderStatus(selectedOrder._id, newStatus);
      await loadOrders();
      setShowStatusModal(false);
      setSelectedOrder(null);
      alert('Order status updated successfully!');
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(error.response?.data?.message || 'Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#ff9800',
      'confirmed': '#2196f3',
      'packed': '#9c27b0',
      'shipped': '#3f51b5',
      'delivered': '#4caf50',
      'cancelled': '#f44336',
      'returned': '#ff5722'
    };
    return statusColors[status?.toLowerCase()] || '#666';
  };

  const getStatusOptions = (currentStatus) => {
    const allStatuses = ['pending', 'confirmed', 'packed', 'shipped', 'delivered', 'cancelled', 'returned'];
    return allStatuses.filter(status => status !== currentStatus);
  };

  const handleViewOrder = async (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsModal(true);
    setLoadingOrderDetails(true);
    try {
      const orderData = await api.getOrder(order._id);
      setOrderDetails(orderData);
    } catch (error) {
      console.error('Error loading order details:', error);
      alert('Failed to load order details');
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      'pending': '⏳',
      'confirmed': '✅',
      'packed': '📦',
      'shipped': '🚚',
      'delivered': '🎉',
      'cancelled': '❌',
      'returned': '↩️'
    };
    return statusIcons[status?.toLowerCase()] || '📋';
  };

  const handlePrintInvoice = () => {
    if (!orderDetails) return;

    const printWindow = window.open('', '_blank');
    const order = orderDetails;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - Order #${order._id.slice(-8).toUpperCase()}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              color: #333;
              line-height: 1.6;
            }
            .invoice-header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 20px;
            }
            .invoice-header h1 {
              color: #667eea;
              font-size: 2.5rem;
              margin-bottom: 10px;
            }
            .invoice-header p {
              color: #666;
              font-size: 1rem;
            }
            .invoice-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 40px;
              flex-wrap: wrap;
            }
            .info-section {
              flex: 1;
              min-width: 250px;
              margin-bottom: 20px;
            }
            .info-section h3 {
              color: #667eea;
              margin-bottom: 10px;
              font-size: 1.2rem;
            }
            .info-section p {
              margin: 5px 0;
              color: #555;
            }
            .order-items {
              margin-bottom: 40px;
            }
            .order-items h3 {
              color: #667eea;
              margin-bottom: 20px;
              font-size: 1.3rem;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              padding: 12px;
              text-align: left;
              border-bottom: 1px solid #ddd;
            }
            th {
              background-color: #667eea;
              color: white;
              font-weight: 600;
            }
            tr:hover {
              background-color: #f5f5f5;
            }
            .text-right {
              text-align: right;
            }
            .totals {
              margin-top: 20px;
              margin-left: auto;
              width: 300px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              padding: 10px 0;
              border-bottom: 1px solid #ddd;
            }
            .total-row.final {
              font-size: 1.3rem;
              font-weight: bold;
              color: #667eea;
              border-top: 2px solid #667eea;
              border-bottom: 2px solid #667eea;
              margin-top: 10px;
              padding-top: 15px;
            }
            .status-badge {
              display: inline-block;
              padding: 5px 15px;
              border-radius: 20px;
              font-weight: 600;
              font-size: 0.9rem;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              color: #666;
              font-size: 0.9rem;
              border-top: 1px solid #ddd;
              padding-top: 20px;
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <h1>AVRNHH</h1>
            <p>Invoice</p>
          </div>
          
          <div class="invoice-info">
            <div class="info-section">
              <h3>Order Information</h3>
              <p><strong>Order ID:</strong> #${order._id.slice(-8).toUpperCase()}</p>
              <p><strong>Order Date:</strong> ${formatDate(order.createdAt)}</p>
              <p><strong>Status:</strong> 
                <span class="status-badge" style="background-color: ${getStatusColor(order.status)}20; color: ${getStatusColor(order.status)}">
                  ${getStatusIcon(order.status)} ${order.status || 'Pending'}
                </span>
              </p>
            </div>
            
            <div class="info-section">
              <h3>Customer Information</h3>
              <p><strong>Name:</strong> ${order.user?.name || 'N/A'}</p>
              <p><strong>Email:</strong> ${order.user?.email || 'N/A'}</p>
              ${order.user?.phone ? `<p><strong>Phone:</strong> ${order.user.phone}</p>` : ''}
            </div>
            
            ${order.deliveryAddress ? `
            <div class="info-section">
              <h3>Delivery Address</h3>
              <p>${order.deliveryAddress.name || order.deliveryAddress.fullName || ''}</p>
              ${order.deliveryAddress.phone ? `<p>📞 ${order.deliveryAddress.phone}</p>` : ''}
              <p>
                ${order.deliveryAddress.street || order.deliveryAddress.line1 || ''}
                ${order.deliveryAddress.floor ? `, Floor ${order.deliveryAddress.floor}` : ''}
                ${order.deliveryAddress.landmark ? `, ${order.deliveryAddress.landmark}` : ''}
                <br>
                ${order.deliveryAddress.area ? `${order.deliveryAddress.area}, ` : ''}
                ${order.deliveryAddress.city}, ${order.deliveryAddress.state} - ${order.deliveryAddress.pincode || order.deliveryAddress.postalCode || ''}
              </p>
            </div>
            ` : ''}
          </div>
          
          <div class="order-items">
            <h3>Order Items</h3>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Size</th>
                  <th class="text-right">Quantity</th>
                  <th class="text-right">Price</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${order.items?.map(item => `
                  <tr>
                    <td>${item.title || item.name || 'Product'}</td>
                    <td>${item.size || item.sku || '-'}</td>
                    <td class="text-right">${item.qty || item.quantity || 1}</td>
                    <td class="text-right">₹${(item.price || 0).toFixed(2)}</td>
                    <td class="text-right">₹${((item.price || 0) * (item.qty || item.quantity || 1)).toFixed(2)}</td>
                  </tr>
                `).join('') || ''}
              </tbody>
            </table>
            
            <div class="totals">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>₹${(order.subtotal || 0).toFixed(2)}</span>
              </div>
              ${order.shipping > 0 ? `
              <div class="total-row">
                <span>Shipping:</span>
                <span>₹${order.shipping.toFixed(2)}</span>
              </div>
              ` : ''}
              <div class="total-row final">
                <span>Total:</span>
                <span>₹${(order.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          <div class="order-items">
            <h3>Payment Information</h3>
            <p><strong>Payment Method:</strong> ${order.paymentMethod === 'cod' ? 'Cash on Delivery (COD)' : order.paymentMethod || 'N/A'}</p>
            ${order.paymentMethod === 'cod' ? `<p>Amount to be paid on delivery: ₹${(order.total || 0).toFixed(2)}</p>` : ''}
          </div>
          
          ${order.notes ? `
          <div class="order-items">
            <h3>Order Notes</h3>
            <p>${order.notes}</p>
          </div>
          ` : ''}
          
          <div class="footer">
            <p>Thank you for your order!</p>
            <p>Generated on ${new Date().toLocaleString('en-IN')}</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return <div className="orders-loading">Loading orders...</div>;
  }

  return (
    <div className="orders-management">
      {/* New Order Alert */}
      {newOrderAlert && (
        <div className="new-order-alert" onClick={() => {
          setNewOrderAlert(null);
          navigate(`/admin/orders/${newOrderAlert.orderId}`);
        }}>
          <div className="alert-content">
            <span className="alert-icon">🔔</span>
            <div className="alert-text">
              <strong>{newOrderAlert.message}</strong>
              <span className="alert-subtext">Click to view order details</span>
            </div>
          </div>
          <button 
            className="alert-close"
            onClick={(e) => {
              e.stopPropagation();
              setNewOrderAlert(null);
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="orders-header">
        <h1>Orders Management</h1>
      </div>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search by Order ID, Customer name/email/phone, or Product name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filters-row">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="packed">Packed</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="returned">Returned</option>
          </select>
          <select
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
            className="filter-select"
          >
            <option value="">All Payment Methods</option>
            <option value="cod">Cash on Delivery (COD)</option>
            <option value="online">Online Payment</option>
            <option value="card">Card Payment</option>
          </select>
          <input
            type="number"
            placeholder="Min Total (₹)"
            value={filterTotalMin}
            onChange={(e) => setFilterTotalMin(e.target.value)}
            className="filter-input"
          />
          <input
            type="number"
            placeholder="Max Total (₹)"
            value={filterTotalMax}
            onChange={(e) => setFilterTotalMax(e.target.value)}
            className="filter-input"
          />
          <input
            type="date"
            placeholder="From Date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="filter-input"
            title="Filter orders from this date"
          />
          <input
            type="date"
            placeholder="To Date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="filter-input"
            title="Filter orders until this date"
          />
          <button
            onClick={clearFilters}
            className="btn-clear-filters"
            title="Clear all filters"
          >
            🗑️ Clear Filters
          </button>
        </div>
        <div className="results-count">
          Showing {filteredOrders.length} of {orders.length} orders
        </div>
      </div>

      <div className="orders-table-container">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <p>No orders found matching your filter criteria.</p>
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order._id}>
                  <td className="order-id-cell">
                    #{order._id.slice(-8).toUpperCase()}
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{order.user?.name || 'N/A'}</div>
                      <div className="customer-email">{order.user?.email || 'N/A'}</div>
                      {order.user?.phone && (
                        <div className="customer-phone">📞 {order.user.phone}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="items-count">{order.items?.length || 0} items</div>
                    <div className="items-preview">
                      {order.items?.slice(0, 2).map((item, idx) => (
                        <div key={idx} className="item-preview">
                          {item.title || item.name} (x{item.qty || item.quantity})
                        </div>
                      ))}
                      {order.items?.length > 2 && (
                        <div className="more-items">+{order.items.length - 2} more</div>
                      )}
                    </div>
                  </td>
                  <td className="total-cell">₹{order.total?.toFixed(2) || '0.00'}</td>
                  <td>
                    <span className="payment-badge">
                      {order.paymentMethod === 'cod' ? '💵 COD' : order.paymentMethod}
                    </span>
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(order.status) + '20',
                        color: getStatusColor(order.status)
                      }}
                    >
                      {order.status || 'Pending'}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(order.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => handleViewOrder(order)}
                      >
                        View
                      </button>
                      <button
                        className="btn-update-status"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowStatusModal(true);
                        }}
                      >
                        Update Status
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderDetailsModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => {
          setShowOrderDetailsModal(false);
          setSelectedOrder(null);
          setOrderDetails(null);
        }}>
          <div className="modal-content order-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {/* Print Invoice button yaha hai but usko remove karna hai */}
                {/* <button
                  className="btn-print-invoice"
                  onClick={handlePrintInvoice}
                  disabled={!orderDetails || loadingOrderDetails}
                  title="Print Invoice"
                >
                  🖨️ Print Invoice
                </button> */}
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowOrderDetailsModal(false);
                    setSelectedOrder(null);
                    setOrderDetails(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="modal-body">
              {loadingOrderDetails ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Loading order details...</p>
                </div>
              ) : orderDetails ? (
                <>
                  {/* Order Info */}
                  <div className="order-detail-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div>
                        <h3>Order #{orderDetails._id.slice(-8).toUpperCase()}</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem' }}>Placed on {formatDate(orderDetails.createdAt)}</p>
                      </div>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: getStatusColor(orderDetails.status) + '20',
                          color: getStatusColor(orderDetails.status)
                        }}
                      >
                        {getStatusIcon(orderDetails.status)} {orderDetails.status || 'Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="order-detail-section">
                    <h4>Customer Information</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                      <div>
                        <strong>Name:</strong> {orderDetails.user?.name || 'N/A'}
                      </div>
                      <div>
                        <strong>Email:</strong> {orderDetails.user?.email || 'N/A'}
                      </div>
                      {orderDetails.user?.phone && (
                        <div>
                          <strong>Phone:</strong> {orderDetails.user.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delivery Address */}
                  {orderDetails.deliveryAddress && (
                    <div className="order-detail-section">
                      <h4>Delivery Address</h4>
                      <div style={{ marginTop: '0.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                        <p><strong>{orderDetails.deliveryAddress.name || orderDetails.deliveryAddress.fullName}</strong></p>
                        {orderDetails.deliveryAddress.phone && (
                          <p>📞 {orderDetails.deliveryAddress.phone}</p>
                        )}
                        <p>
                          {orderDetails.deliveryAddress.street || orderDetails.deliveryAddress.line1}
                          {orderDetails.deliveryAddress.floor && `, Floor ${orderDetails.deliveryAddress.floor}`}
                          {orderDetails.deliveryAddress.landmark && `, ${orderDetails.deliveryAddress.landmark}`}
                          <br />
                          {orderDetails.deliveryAddress.area && `${orderDetails.deliveryAddress.area}, `}
                          {orderDetails.deliveryAddress.city}, {orderDetails.deliveryAddress.state} - {orderDetails.deliveryAddress.pincode || orderDetails.deliveryAddress.postalCode}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="order-detail-section">
                    <h4>Order Items ({orderDetails.items?.length || 0})</h4>
                    <div style={{ marginTop: '0.5rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd', width: '60px' }}>Image</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Item</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Size</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Qty</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Price</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderDetails.items?.map((item, index) => {
                            // Extract product image - similar to OrderDetails.js logic
                            let productImage = null;
                            
                            // Check if product is populated (object with data)
                            if (item.product && typeof item.product === 'object' && item.product !== null) {
                              const isPopulatedProduct = item.product._id || item.product.title || item.product.name;
                              
                              if (isPopulatedProduct) {
                                if (item.product.images) {
                                  if (Array.isArray(item.product.images) && item.product.images.length > 0) {
                                    productImage = item.product.images.find(img => img && img.trim()) || null;
                                  } else if (typeof item.product.images === 'string' && item.product.images.trim()) {
                                    productImage = item.product.images;
                                  }
                                }
                              }
                            }
                            
                            // Fallback to item-level images if product image not found
                            if (!productImage) {
                              if (item.images) {
                                if (Array.isArray(item.images) && item.images.length > 0) {
                                  productImage = item.images.find(img => img && img.trim()) || null;
                                } else if (typeof item.images === 'string' && item.images.trim()) {
                                  productImage = item.images;
                                }
                              } else if (item.image && typeof item.image === 'string' && item.image.trim()) {
                                productImage = item.image;
                              }
                            }
                            
                            // Convert to display URL or use placeholder
                            const imageUrl = productImage 
                              ? convertGoogleDriveUrlForDisplay(productImage) 
                              : getPlaceholderImage(50, 50, 'No Image');
                            
                            return (
                              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '0.75rem', verticalAlign: 'middle' }}>
                                  <img 
                                    src={imageUrl}
                                    alt={item.title || item.name || 'Product'}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #e0e0e0'
                                    }}
                                    onError={(e) => {
                                      // Prevent infinite retry loops
                                      if (e.target.dataset.retryCount) {
                                        e.target.src = getPlaceholderImage(50, 50, 'No Image');
                                        e.target.onerror = null;
                                        return;
                                      }
                                      e.target.dataset.retryCount = '1';
                                      
                                      const originalSrc = e.target.src;
                                      // Skip problematic URLs
                                      if (originalSrc.includes('lh3.googleusercontent.com') || 
                                          originalSrc.includes('drive.google.com/uc?export=download')) {
                                        e.target.src = getPlaceholderImage(50, 50, 'No Image');
                                        e.target.onerror = null;
                                        return;
                                      }
                                      
                                      // Try thumbnail method if view fails (only for Google Drive URLs)
                                      if (originalSrc.includes('drive.google.com')) {
                                        const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                                          originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                                        if (fileIdMatch && fileIdMatch[1] && !originalSrc.includes('thumbnail')) {
                                          e.target.src = `https://drive.google.com/thumbnail?id=${fileIdMatch[1]}&sz=w200`;
                                          return;
                                        }
                                      }
                                      
                                      e.target.src = getPlaceholderImage(50, 50, 'No Image');
                                      e.target.onerror = null;
                                    }}
                                  />
                                </td>
                                <td style={{ padding: '0.75rem' }}>{item.title || item.name || 'Product'}</td>
                                <td style={{ padding: '0.75rem' }}>{item.size || item.sku || '-'}</td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.qty || item.quantity || 1}</td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>₹{(item.price || 0).toFixed(2)}</td>
                                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                                  ₹{((item.price || 0) * (item.qty || item.quantity || 1)).toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="order-detail-section">
                    <h4>Order Summary</h4>
                    <div style={{ marginTop: '0.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Subtotal:</span>
                        <span>₹{(orderDetails.subtotal || 0).toFixed(2)}</span>
                      </div>
                      {orderDetails.shipping > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span>Shipping:</span>
                          <span>₹{orderDetails.shipping.toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #ddd', fontSize: '1.2rem', fontWeight: 'bold', color: '#667eea' }}>
                        <span>Total:</span>
                        <span>₹{(orderDetails.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="order-detail-section">
                    <h4>Payment Information</h4>
                    <div style={{ marginTop: '0.5rem' }}>
                      <p><strong>Payment Method:</strong> {orderDetails.paymentMethod === 'cod' ? '💵 Cash on Delivery (COD)' : orderDetails.paymentMethod || 'N/A'}</p>
                      {orderDetails.paymentMethod === 'cod' && (
                        <p style={{ color: '#666', marginTop: '0.5rem' }}>
                          Amount to be paid on delivery: ₹{(orderDetails.total || 0).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Order Notes */}
                  {orderDetails.notes && (
                    <div className="order-detail-section">
                      <h4>Order Notes</h4>
                      <div style={{ marginTop: '0.5rem', padding: '1rem', background: '#fff9e6', borderRadius: '8px', border: '1px solid #ffd700' }}>
                        <p>{orderDetails.notes}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Failed to load order details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => {
          setShowStatusModal(false);
          setSelectedOrder(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Order Status</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedOrder(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p><strong>Order ID:</strong> #{selectedOrder._id.slice(-8).toUpperCase()}</p>
              <p><strong>Current Status:</strong> 
                <span
                  className="current-status"
                  style={{ color: getStatusColor(selectedOrder.status) }}
                >
                  {selectedOrder.status || 'Pending'}
                </span>
              </p>
              <div className="status-options">
                <label>Select New Status:</label>
                <div className="status-buttons">
                  {getStatusOptions(selectedOrder.status).map((status) => (
                    <button
                      key={status}
                      className="status-option-btn"
                      onClick={() => handleStatusUpdate(status)}
                      disabled={updatingStatus}
                      style={{
                        backgroundColor: getStatusColor(status) + '20',
                        color: getStatusColor(status),
                        borderColor: getStatusColor(status)
                      }}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

